/**
 * ============================================================
 * EMMA GYŐR — Cart reservation timer (business-critical)
 * ============================================================
 * Countdown banner shown in the cart drawer ("A kosár csak 10:00
 * percig van fenntartva!"). This is a conversion/urgency element —
 * do not remove, disable, or silently break this file. If a change
 * elsewhere in the theme needs to touch cart markup or cart events,
 * re-test this timer afterwards (add a product to an empty cart and
 * confirm the banner appears in the drawer).
 *
 * Root cause of the bug this file fixes (2026-08-19): the theme's
 * AJAX add-to-cart flow (theme.dev.js CartItems.build()) only ever
 * replaces the line-items area of the cart drawer. It never re-runs
 * the Liquid that surrounds it, so any markup that depended on being
 * freshly rendered after add-to-cart (like this timer used to) never
 * appeared for a customer whose cart started empty — which is the
 * normal case. Fix: the Liquid snippet now always outputs the timer
 * markup (see snippets/cart-reservation-timer.liquid), and this
 * script is the only thing responsible for showing/hiding it and
 * running the countdown, driven by the theme's own `theme:cart:change`
 * event (the same event the theme's built-in <cart-count> element
 * relies on), not by guessing when the DOM has been rebuilt.
 *
 * Why this is its own file instead of inline in layout/theme.liquid:
 * that file is long, edited often for unrelated reasons, and inline
 * <script> blocks inside it have gone missing before. One dedicated
 * file, loaded by one clearly-labeled <script> tag, is much harder
 * to delete by accident.
 */
(function () {
  'use strict';

  var START_KEY = 'egCartTimerStart';
  var EMPTIED_KEY = 'egCartTimerWasEmptied';
  var DURATION_SECONDS = 600;
  var lastKnownCartCount = null;

  function getTimerEls() {
    return document.querySelectorAll('[data-eg-cart-timer]');
  }

  function setVisible(el, visible) {
    if (visible) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  function cartHasItems() {
    if (lastKnownCartCount !== null) return lastKnownCartCount > 0;

    // Before the first theme:cart:change event has fired (e.g. a full
    // page load), trust whatever count the theme already rendered
    // server-side into the header/cart badge.
    var countEl = document.querySelector('[data-cart-count]');
    if (countEl) {
      var raw = countEl.getAttribute('data-cart-count') || countEl.textContent || '0';
      var n = parseInt(raw, 10);
      if (!isNaN(n)) return n > 0;
    }
    return false;
  }

  function render() {
    var timerEls = getTimerEls();
    if (timerEls.length === 0) return;

    if (!cartHasItems()) {
      sessionStorage.setItem(EMPTIED_KEY, '1');
      timerEls.forEach(function (el) {
        setVisible(el, false);
      });
      return;
    }

    var start = sessionStorage.getItem(START_KEY);
    var wasEmptied = sessionStorage.getItem(EMPTIED_KEY) === '1';

    if (!start || wasEmptied) {
      start = Date.now();
      sessionStorage.setItem(START_KEY, start);
      sessionStorage.removeItem(EMPTIED_KEY);
    } else {
      start = parseInt(start, 10);
    }

    var elapsed = Math.floor((Date.now() - start) / 1000);

    if (elapsed >= DURATION_SECONDS) {
      // Restart the countdown instead of freezing at 00:00 or hiding
      // the banner — on a tab left open for a while that looked like
      // the banner had "disappeared".
      start = Date.now();
      sessionStorage.setItem(START_KEY, start);
      elapsed = 0;
    }

    var remaining = DURATION_SECONDS - elapsed;
    var mm = Math.floor(remaining / 60).toString().padStart(2, '0');
    var ss = (remaining % 60).toString().padStart(2, '0');
    var text = mm + ':' + ss;

    timerEls.forEach(function (el) {
      setVisible(el, true);
      var valueEl = el.querySelector('[data-eg-cart-timer-value]');
      if (valueEl) valueEl.textContent = text;
    });
  }

  document.addEventListener('theme:cart:change', function (event) {
    var detail = event && event.detail;
    if (detail && typeof detail.cartCount === 'number') {
      lastKnownCartCount = detail.cartCount;
    }
    render();
  });

  // Safety net: theme:product:added fires once the drawer has fully
  // finished re-rendering after add-to-cart. Re-checking here costs
  // nothing and covers us if theme:cart:change is ever removed.
  document.addEventListener('theme:product:added', render);

  render();

  if (!window.egCartTimerInterval) {
    window.egCartTimerInterval = setInterval(render, 1000);
  }
})();
